import { EditOutline18Icon } from "@vygruppen/spor-icon-react";
import { format } from "date-fns";
import { InternalMessageResponse } from "features/CenterContent/RoleContent/InternalMessage/schema/responseSchema";
import { Badges } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/Badges";
import RelatedEventInfo from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/components/RelatedEventInfo";
import { INTERNAL_EVENTS_QUERY_KEY } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/EventsOverviewButtonAndModal";
import {
  getBadgeExtra,
  getBadgeItem,
  getCategory,
  getDisplayDurationFromVisibility,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/utils";
import { DepublishInformation } from "features/OperationalInformation/DepublishInformation";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import {
  DropsBadge,
  Flex,
  HStack,
  IconButton,
  Stack,
  Text,
  VStack,
} from "shared/components/Spor";
import { visibilitySchemaResponse } from "shared/form/visibilitySchema";
import { useDropsRole } from "stores/useDropsRole";
import { useTheme } from "styled-components";

type InternalMessageEventsProps = {
  data: InternalMessageResponse[];
  onEditClick: (event: InternalMessageResponse) => void;
};

export const InternalMessageEventsList = ({
  data,
  onEditClick,
}: InternalMessageEventsProps) => {
  const theme = useTheme();
  const { isVaktleder } = useDropsRole();
  if (data.length === 0)
    return (
      <NoDataMessage reason="Det er ikke registrert noen hendelser med denne filtreringen" />
    );
  return (
    <Stack gap="18px">
      {data.map((event) => {
        const text = event.customTexts[0].texts.NOB;
        const BadgeItem = getBadgeItem(event);
        const BadgeExtra = getBadgeExtra(event);
        const category = getCategory(event);
        return (
          <VStack
            key={event.uuid}
            background={theme.colorBackgroundTertiary}
            borderRadius="sm"
            p="18px"
            justifyItems="space-between"
            alignItems="flex-start"
            gap="12px"
            position="relative"
          >
            <RelatedEventInfo event={event} />
            <Flex w="100%" flexWrap="wrap-reverse">
              <Flex flexDir="column" gap="12px" margin="auto 0">
                {BadgeItem && (
                  <Flex gap="12px">
                    <DropsBadge semantics="info">{BadgeItem}</DropsBadge>
                    {BadgeExtra && (
                      <DropsBadge semantics="info">{BadgeExtra}</DropsBadge>
                    )}
                  </Flex>
                )}
                <Flex
                  // BadgeItem decides the width instead if it exists (necessary for wrap)
                  w={BadgeItem ? "0px" : undefined}
                >
                  <Badges
                    minW="max-content" // necessary when parent is 0px
                    eventCategory={category}
                    displayDuration={getDisplayDurationFromVisibility(
                      visibilitySchemaResponse.parse(event.visibility),
                    )}
                  />
                </Flex>
              </Flex>
              {isVaktleder() && (
                <HStack minWidth="fit-content" ml="auto" mb="auto">
                  <DepublishInformation
                    uuid={event.uuid}
                    disabled={false}
                    queryKey={[INTERNAL_EVENTS_QUERY_KEY]}
                  />
                  <IconButton
                    onClick={() => onEditClick(event)}
                    size="sm"
                    aria-label="Rediger"
                    variant="tertiary"
                    icon={<EditOutline18Icon />}
                    disabled={!!event.evaluationForm}
                  />
                </HStack>
              )}
            </Flex>
            <Text>{text?.description}</Text>
            <Text variant="secondary">
              {format(
                event.updatedAt,
                "'Sist oppdatert' dd.MM.yyyy 'kl.' HH:mm",
              )}
            </Text>
          </VStack>
        );
      })}
    </Stack>
  );
};
